import type * as ExcelJS from 'exceljs';
import { Entitlement, OrgEntitlement, Product, ProductAddonConfig, SKU } from '../types';

export interface SKUExportLink {
  sku_id: number;
  sku_name: string;
  buying_model?: string;
  is_external: boolean;
  entitlements: Entitlement[];
  orgEntitlements: OrgEntitlement[];
}

export interface SKUExportProduct extends Product {
  skuLinks: SKUExportLink[];
}

export interface CommercialSKUExportInput {
  products: SKUExportProduct[];
  skus: SKU[];
  addonConfigs: ProductAddonConfig[];
}

const workbookMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const navy = '082B45';
const blue = '1473E6';
const paleBlue = 'EAF3FB';
const borderColor = 'D7E0E8';

const uniqueBy = <T,>(items: T[], getKey: (item: T) => string): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getKey(item);
    if (seen.has(key)) { return false; }
    seen.add(key);
    return true;
  });
};

const setupDataSheet = (sheet: ExcelJS.Worksheet, title: string, description: string, headers: string[]) => {
  sheet.views = [{ state: 'frozen', ySplit: 4 }];
  sheet.mergeCells(1, 1, 1, headers.length);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { name: 'Aptos Display', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${navy}` } };
  titleCell.alignment = { vertical: 'middle' };
  sheet.getRow(1).height = 30;
  sheet.mergeCells(2, 1, 2, headers.length);
  const descriptionCell = sheet.getCell(2, 1);
  descriptionCell.value = description;
  descriptionCell.font = { name: 'Aptos', size: 10, color: { argb: 'FF52616F' }, italic: true };
  descriptionCell.alignment = { vertical: 'middle', wrapText: true };
  sheet.getRow(2).height = 30;

  const headerRow = sheet.getRow(4);
  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { name: 'Aptos', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${blue}` } };
    cell.alignment = { vertical: 'middle', wrapText: true };
    cell.border = { bottom: { style: 'medium', color: { argb: `FF${navy}` } } };
  });
  headerRow.height = 34;
};

const styleBodyRows = (sheet: ExcelJS.Worksheet, startRow = 5) => {
  for (let rowNumber = startRow; rowNumber <= sheet.rowCount; rowNumber += 1) {
    const row = sheet.getRow(rowNumber);
    row.height = 32;
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { name: 'Aptos', size: 10, color: { argb: 'FF1C2730' } };
      cell.alignment = { vertical: 'top', wrapText: true };
      cell.border = { bottom: { style: 'hair', color: { argb: `FF${borderColor}` } } };
      if (rowNumber % 2 === 0) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${paleBlue}` } };
      }
    });
  }
};

const downloadWorkbook = async (workbook: ExcelJS.Workbook, filename: string): Promise<void> => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer as BlobPart], { type: workbookMimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const skuDescription = (skuId: number, products: SKUExportProduct[]): string => {
  const linkedEntitlements = products.flatMap((product) =>
    product.skuLinks
      .filter((link) => link.sku_id === skuId)
      .flatMap((link) => [
        ...link.entitlements.map((item) => ({ kind: 'standard', item })),
        ...link.orgEntitlements.map((item) => ({ kind: 'org', item })),
      ]),
  );
  const uniqueEntitlements = uniqueBy(linkedEntitlements, ({ kind, item }) =>
    `${kind}:${item.id ?? `${item.product_id}:${item.name}:${item.value}`}`,
  );
  return uniqueEntitlements.length === 1 ? uniqueEntitlements[0].item.description || '' : '';
};

const addonListByProduct = (
  configs: ProductAddonConfig[],
  skuById: Map<number, SKU>,
  descriptionBySku: Map<number, string>,
  productId: number,
): string => configs
  .filter((config) => config.base_product_id === productId)
  .map((config) => {
    const addon = skuById.get(config.sku_id);
    if (!addon || addon.is_dev_test !== false) { return undefined; }
    const description = descriptionBySku.get(config.sku_id) || '';
    const addonType = config.addon_type ? ` (${config.addon_type})` : '';
    return description ? `${addon.name}${addonType} — ${description}` : `${addon.name}${addonType}`;
  })
  .filter((value): value is string => Boolean(value))
  .filter((value, index, all) => all.indexOf(value) === index)
  .join('\n');

/** Export commercially available SKUs into a formatted Excel workbook. */
export const exportCommercialSKUs = async (input: CommercialSKUExportInput): Promise<{
  exportedCount: number;
  excludedDevTestCount: number;
  filename: string;
}> => {
  const excelModule = await import('exceljs');
  const productionSkus = input.skus.filter((sku) => sku.is_dev_test === false && sku.id !== undefined);
  const skuById = new Map(productionSkus.map((sku) => [sku.id as number, sku]));
  const productById = new Map(input.products.filter((product) => product.id !== undefined).map((product) => [product.id as number, product]));
  const addonSkuIds = new Set(input.addonConfigs.map((config) => config.sku_id));
  const descriptionBySku = new Map(productionSkus.map((sku) => [sku.id as number, skuDescription(sku.id as number, input.products)]));

  const skuGroups = productionSkus.map((sku) => ({
    sku,
    products: input.products
      .filter((product) => product.skuLinks.some((link) => link.sku_id === sku.id))
      .sort((left, right) => left.name.localeCompare(right.name)),
  }));

  const standaloneGroups = skuGroups
    .filter((group) => !addonSkuIds.has(group.sku.id as number) && group.products.length === 1)
    .sort((left, right) => left.products[0].name.localeCompare(right.products[0].name) || left.sku.name.localeCompare(right.sku.name));
  const suiteGroups = skuGroups.filter((group) => !addonSkuIds.has(group.sku.id as number) && group.products.length > 1);
  const addonGroups = skuGroups.filter((group) => addonSkuIds.has(group.sku.id as number));
  const unlinkedGroups = skuGroups.filter((group) => !addonSkuIds.has(group.sku.id as number) && group.products.length === 0);
  const unlinkedSkuCount = unlinkedGroups.length;

  const workbook = new excelModule.Workbook();
  workbook.creator = 'Product Onboarding System';
  workbook.subject = 'Commercially available production SKUs and product relationships';
  workbook.title = 'Production SKU Catalog';
  workbook.keywords = 'production SKU, standalone SKU, suite SKU, product catalog';

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const exportedAt = new Date();
  const exportInfo = workbook.addWorksheet('Export Info');
  exportInfo.columns = [{ width: 34 }, { width: 72 }];
  exportInfo.mergeCells('A1:B1');
  exportInfo.getCell('A1').value = 'Commercial Production SKU Catalog';
  exportInfo.getCell('A1').font = { name: 'Aptos Display', size: 18, bold: true, color: { argb: 'FFFFFFFF' } };
  exportInfo.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${navy}` } };
  exportInfo.getRow(1).height = 34;
  [
    ['Export date & time', exportedAt.toLocaleString('en-GB', { timeZone, hour12: false })],
    ['Timezone', timeZone],
    ['Production filter', 'SKU is included only when is_dev_test is false'],
    ['Production SKUs in source', productionSkus.length],
    ['Standalone SKUs exported', standaloneGroups.length],
    ['Suite SKUs exported', suiteGroups.length],
    ['Add-on SKUs exported', addonGroups.length],
    ['Production SKUs without product links', unlinkedSkuCount],
    ['Development/test SKUs excluded', input.skus.filter((sku) => sku.is_dev_test === true).length],
  ].forEach((row, index) => {
    const rowNumber = index + 3;
    exportInfo.getCell(rowNumber, 1).value = row[0];
    exportInfo.getCell(rowNumber, 2).value = row[1];
    exportInfo.getCell(rowNumber, 1).font = { name: 'Aptos', size: 10, bold: true };
    exportInfo.getCell(rowNumber, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${paleBlue}` } };
    exportInfo.getCell(rowNumber, 2).font = { name: 'Aptos', size: 10 };
    exportInfo.getCell(rowNumber, 2).alignment = { wrapText: true, vertical: 'middle' };
    exportInfo.getRow(rowNumber).height = 26;
  });
  exportInfo.getCell('A13').value = 'Workbook contents';
  exportInfo.getCell('A13').font = { bold: true, name: 'Aptos', size: 10 };
  exportInfo.getCell('B13').value = 'Standalone SKUs are grouped by product. Suite SKUs have one row per included product, with the suite name and product count merged across its rows. SKUs configured in product_addon_config are listed separately as add-ons.';
  exportInfo.getCell('B13').alignment = { wrapText: true, vertical: 'top' };
  exportInfo.getRow(13).height = 44;
  exportInfo.views = [{ state: 'frozen', ySplit: 2 }];

  const standaloneSheet = workbook.addWorksheet('Standalone SKUs');
  const standaloneHeaders = [
    'Product', 'Product Key', 'SKU Name', 'Buying Model', 'External',
    'Entitlement Description (only when exactly one entitlement is linked)', 'Allowed Add-on SKUs / Single Entitlement Description',
  ];
  setupDataSheet(standaloneSheet, 'Standalone SKUs', 'Commercial SKUs linked to exactly one product. Rows are sorted and grouped by product; product values repeat so the table remains filterable.', standaloneHeaders);
  standaloneSheet.columns = [
    { width: 34 }, { width: 20 }, { width: 38 }, { width: 18 }, { width: 12 }, { width: 56 }, { width: 64 },
  ];
  standaloneGroups.forEach(({ sku, products }) => {
    const product = products[0];
    const link = product.skuLinks.find((item) => item.sku_id === sku.id);
    if (!link) { return; }
    standaloneSheet.addRow([
      product.name,
      product.key || '',
      sku.name,
      sku.subscription_buying_model || link.buying_model || '',
      sku.is_external ? 'Yes' : 'No',
      descriptionBySku.get(sku.id as number) || '',
      addonListByProduct(input.addonConfigs, skuById, descriptionBySku, product.id ?? -1),
    ]);
  });
  standaloneSheet.autoFilter = { from: 'A4', to: `G${Math.max(4, standaloneSheet.rowCount)}` };
  styleBodyRows(standaloneSheet);

  const addonSheet = workbook.addWorksheet('Add-on SKUs');
  const addonHeaders = ['Add-on SKU Name', 'Buying Model', 'Base Product', 'Product Key', 'External', 'Add-on Type', 'Description'];
  setupDataSheet(addonSheet, 'Add-on SKUs', 'SKUs identified by product_addon_config are listed here and excluded from the Standalone SKUs and Suite SKUs sheets.', addonHeaders);
  addonSheet.columns = [{ width: 38 }, { width: 18 }, { width: 34 }, { width: 20 }, { width: 12 }, { width: 24 }, { width: 56 }];
  input.addonConfigs.forEach((config) => {
    const sku = skuById.get(config.sku_id);
    if (!sku) { return; }
    const baseProduct = productById.get(config.base_product_id);
    addonSheet.addRow([
      sku.name,
      sku.subscription_buying_model || '',
      baseProduct?.name || `Product ID ${config.base_product_id}`,
      baseProduct?.key || '',
      sku.is_external ? 'Yes' : 'No',
      config.addon_type || '',
      config.sku_description || descriptionBySku.get(config.sku_id) || '',
    ]);
  });
  addonSheet.autoFilter = { from: 'A4', to: `G${Math.max(4, addonSheet.rowCount)}` };
  styleBodyRows(addonSheet);

  const suiteSheet = workbook.addWorksheet('Suite SKUs');
  const suiteHeaders = ['SKU Name', 'Product Count', 'Buying Model', 'Product Included', 'Product Key', 'External', 'Allowed Add-on SKUs / Single Entitlement Description'];
  setupDataSheet(suiteSheet, 'Suite SKUs', 'Each suite is listed once per included product. Suite name and product count are merged vertically across the included-product rows.', suiteHeaders);
  suiteSheet.columns = [
    { width: 38 }, { width: 15 }, { width: 18 }, { width: 34 }, { width: 20 }, { width: 12 }, { width: 64 },
  ];
  const mergeGroups: Array<{ start: number; end: number }> = [];
  suiteGroups.forEach(({ sku, products }) => {
    const firstRow = suiteSheet.rowCount + 1;
    products.forEach((product) => {
      const link = product.skuLinks.find((item) => item.sku_id === sku.id);
      suiteSheet.addRow([
        sku.name,
        products.length,
        sku.subscription_buying_model || link?.buying_model || '',
        product.name,
        product.key || '',
        sku.is_external ? 'Yes' : 'No',
        addonListByProduct(input.addonConfigs, skuById, descriptionBySku, product.id ?? -1),
      ]);
    });
    const lastRow = suiteSheet.rowCount;
    if (lastRow > firstRow) {
      suiteSheet.mergeCells(firstRow, 1, lastRow, 1);
      suiteSheet.mergeCells(firstRow, 2, lastRow, 2);
      mergeGroups.push({ start: firstRow, end: lastRow });
    }
  });
  suiteSheet.autoFilter = { from: 'A4', to: `G${Math.max(4, suiteSheet.rowCount)}` };
  styleBodyRows(suiteSheet);
  mergeGroups.forEach(({ start }) => {
    [1, 2].forEach((column) => {
      suiteSheet.getCell(start, column).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      suiteSheet.getCell(start, column).font = { name: 'Aptos', size: 10, bold: true, color: { argb: 'FF1C2730' } };
      suiteSheet.getCell(start, column).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${paleBlue}` } };
    });
  });

  const unlinkedSheet = workbook.addWorksheet('Unlinked SKUs');
  const unlinkedHeaders = ['SKU Name', 'Buying Model', 'External', 'Classification'];
  setupDataSheet(unlinkedSheet, 'Production SKUs Without Product Links', 'These production SKUs are included in the export but cannot be grouped as standalone or suite until a product relationship is configured.', unlinkedHeaders);
  unlinkedSheet.columns = [{ width: 42 }, { width: 20 }, { width: 12 }, { width: 68 }];
  unlinkedGroups.forEach(({ sku }) => {
    unlinkedSheet.addRow([
      sku.name,
      sku.subscription_buying_model || '',
      sku.is_external ? 'Yes' : 'No',
      'No product relationship is configured',
    ]);
  });
  unlinkedSheet.autoFilter = { from: 'A4', to: `D${Math.max(4, unlinkedSheet.rowCount)}` };
  styleBodyRows(unlinkedSheet);

  const dateStamp = exportedAt.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  const filename = `production_sku_catalog_${dateStamp}.xlsx`;
  await downloadWorkbook(workbook, filename);
  return {
    exportedCount: productionSkus.length,
    excludedDevTestCount: input.skus.filter((sku) => sku.is_dev_test === true).length,
    filename,
  };
};

export const hasCompleteDevTestFlags = (skus: SKU[]): boolean =>
  skus.length > 0 && skus.every((sku) => typeof sku.is_dev_test === 'boolean');
